import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ListChecks, RefreshCw, AlertOctagon, ShieldQuestion, Copy, Archive } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = ["rate_limit", "csrf", "new_device", "authentication", "tradeguard", "verification", "approval", "other"];
const CLASSIFICATIONS = ["false_positive", "expected_behavior", "legitimate_threat", "needs_investigation", "duplicate", "resolved"];
const CLS_COLORS = {
  false_positive: "bg-green-100 text-green-700", expected_behavior: "bg-blue-100 text-blue-700",
  legitimate_threat: "bg-red-100 text-red-700", needs_investigation: "bg-amber-100 text-amber-700",
  duplicate: "bg-slate-100 text-slate-500", resolved: "bg-green-100 text-green-700",
};
const decode = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.error === "mfa_required") return "MFA required — complete multi-factor auth before triage writes.";
  return formatApiError(d);
};

function Card({ label, value, cls }) {
  return (
    <div className={`rounded-lg border p-3 ${cls || "border-slate-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-xl font-semibold text-[#0A0F1C]">{value}</div>
    </div>
  );
}

export default function TriageCenter() {
  const [metrics, setMetrics] = useState(null);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [skip, setSkip] = useState(0);
  const [f, setF] = useState({ category: "", classification: "", status: "open", critical_only: false, endpoint: "" });

  const updateF = (patch) => { setSkip(0); setF((prev) => ({ ...prev, ...patch })); };

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 50, skip, status: f.status };
    if (f.category) params.category = f.category;
    if (f.classification) params.classification = f.classification;
    if (f.critical_only) params.critical_only = true;
    if (f.endpoint) params.endpoint = f.endpoint;
    Promise.all([
      api.get("/admin/security/dashboard/triage/metrics", { params: { window_days: 30 } }),
      api.get("/admin/security/dashboard/triage/queue", { params }),
    ]).then(([m, q]) => { setMetrics(m.data); setQueue(q.data); setSel({}); })
      .catch((e) => toast.error(decode(e)))
      .finally(() => setLoading(false));
  }, [f, skip]);

  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);

  const classifyOne = async (event_id, classification) => {
    if (classification === "duplicate") return toast.error("Use 'Merge duplicates' (select the canonical event first).");
    const reason = window.prompt(`Reason for classifying as "${classification}" (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A reason is required.");
    try {
      await api.post("/admin/security/dashboard/triage/classify", { event_id, classification, reason });
      toast.success("Event classified."); load();
    } catch (e) { toast.error(decode(e)); }
  };

  const bulkClassify = async (classification) => {
    if (!selectedIds.length) return;
    const reason = window.prompt(`Reason for bulk classifying ${selectedIds.length} event(s) as "${classification}" (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A reason is required.");
    try {
      const { data } = await api.post("/admin/security/dashboard/triage/bulk-classify", { event_ids: selectedIds, classification, reason });
      toast.success(`${data.classified} classified${data.failed?.length ? `, ${data.failed.length} failed` : ""}.`); load();
    } catch (e) { toast.error(decode(e)); }
  };

  const mergeDuplicates = async () => {
    if (selectedIds.length < 2) return toast.error("Select the canonical event + at least one duplicate.");
    const [canonical_event_id, ...duplicate_event_ids] = selectedIds;
    const reason = window.prompt(`Merge ${duplicate_event_ids.length} event(s) as duplicates of the first-selected canonical event (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A reason is required.");
    try {
      const { data } = await api.post("/admin/security/dashboard/triage/merge-duplicates", { canonical_event_id, duplicate_event_ids, reason });
      toast.success(`${data.merged} merged as duplicates.`); load();
    } catch (e) { toast.error(decode(e)); }
  };

  const archiveSelected = async () => {
    if (!selectedIds.length) return;
    const reason = window.prompt(`Reason for archiving ${selectedIds.length} reviewed event(s) (required). Events are never deleted:`, "");
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A reason is required.");
    try {
      const { data } = await api.post("/admin/security/dashboard/triage/archive", { event_ids: selectedIds, reason });
      toast.success(`${data.archived} archived${data.failed?.length ? `, ${data.failed.length} skipped (not reviewed)` : ""}.`); load();
    } catch (e) { toast.error(decode(e)); }
  };

  const m = metrics || {};
  const items = queue?.items || [];

  return (
    <div className="border border-slate-200 bg-white rounded-lg p-5 space-y-4" data-testid="triage-center">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading font-semibold text-[#0A0F1C]">
          <ListChecks size={17} className="text-[#2563EB]" /> Monitor Event Triage Center
        </div>
        <button onClick={load} data-testid="triage-refresh" className="h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-600 flex items-center gap-1.5 hover:bg-slate-50">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {m.readiness_impact && (
        <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${m.readiness_impact.blocks_readiness ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`} data-testid="triage-readiness-impact">
          <AlertOctagon size={18} className="shrink-0 mt-0.5" />
          <div>
            <b>{m.readiness_impact.blocks_readiness ? `${m.readiness_impact.unresolved_critical} real-origin unresolved critical event(s) are blocking enforcement readiness` : "No real-origin unresolved critical events — readiness gate clear"}</b>
            <div className="text-xs opacity-80 mt-0.5">{m.readiness_impact.note}</div>
            <div className="text-[11px] opacity-70 mt-1" data-testid="triage-provenance-views">Readiness-eligible (real): {m.readiness_impact.unresolved_critical} · All origins: {m.readiness_impact.unresolved_critical_all} · By origin: {Object.entries(m.readiness_impact.critical_by_origin || {}).map(([k, v]) => `${k}=${v}`).join(", ") || "—"}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="triage-metrics">
        <Card label="Total (30d)" value={m.total ?? "—"} />
        <Card label="Reviewed" value={m.reviewed ?? "—"} />
        <Card label="Remaining" value={m.remaining ?? "—"} cls={m.remaining ? "border-amber-200 bg-amber-50" : ""} />
        <Card label="Unresolved critical (real / all)" value={m.real_unresolved_critical != null ? `${m.real_unresolved_critical} / ${m.unresolved_critical}` : "—"} cls={m.real_unresolved_critical ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"} />
        <Card label="False-positive %" value={m.false_positive_pct != null ? `${m.false_positive_pct}%` : "—"} />
        <Card label="Threat %" value={m.threat_pct != null ? `${m.threat_pct}%` : "—"} />
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="triage-filters">
        <select value={f.status} onChange={(e) => updateF({ status: e.target.value })} data-testid="triage-filter-status" className="h-9 px-2 rounded-md border border-slate-200 text-sm">
          {["open", "reviewed", "archived", "all"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={f.category} onChange={(e) => updateF({ category: e.target.value })} data-testid="triage-filter-category" className="h-9 px-2 rounded-md border border-slate-200 text-sm">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={f.classification} onChange={(e) => updateF({ classification: e.target.value })} data-testid="triage-filter-classification" className="h-9 px-2 rounded-md border border-slate-200 text-sm">
          <option value="">Any classification</option>
          {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </select>
        <input value={f.endpoint} onChange={(e) => updateF({ endpoint: e.target.value })} data-testid="triage-filter-endpoint" placeholder="endpoint contains…" className="h-9 px-2 rounded-md border border-slate-200 text-sm w-44" />
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={f.critical_only} onChange={(e) => updateF({ critical_only: e.target.checked })} data-testid="triage-filter-critical" /> critical only
        </label>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-2" data-testid="triage-bulk-bar">
          <span className="text-sm text-blue-700 font-medium">{selectedIds.length} selected</span>
          <select onChange={(e) => { if (e.target.value) { bulkClassify(e.target.value); e.target.value = ""; } }} data-testid="triage-bulk-classify" className="h-8 px-2 rounded-md border border-slate-200 text-sm">
            <option value="">Bulk classify as…</option>
            {CLASSIFICATIONS.filter((c) => c !== "duplicate").map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
          <button onClick={mergeDuplicates} data-testid="triage-merge" className="h-8 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1"><Copy size={13} /> Merge duplicates</button>
          <button onClick={archiveSelected} data-testid="triage-archive" className="h-8 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1"><Archive size={13} /> Archive reviewed</button>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="triage-queue">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="px-3 py-2 text-left w-8"></th>
              <th className="px-3 py-2 text-left">Event</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-left">Endpoint</th>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Classify</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-300">No events match these filters.</td></tr>
            ) : items.map((ev) => (
              <tr key={ev.event_id} className="hover:bg-slate-50" data-testid={`triage-row-${ev.event_id}`}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={!!sel[ev.event_id]} onChange={(e) => setSel({ ...sel, [ev.event_id]: e.target.checked })} data-testid={`triage-select-${ev.event_id}`} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-[#0A0F1C]">{ev.event_type}</span>
                    {ev.critical && <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-red-100 text-red-700 flex items-center gap-0.5"><ShieldQuestion size={9} /> critical</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{ev.category}</td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono max-w-[180px] truncate">{ev.endpoint || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-400">{(ev.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2">
                  {ev.triage ? (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${CLS_COLORS[ev.triage.classification] || "bg-slate-100"}`}>
                      {ev.triage.classification.replace(/_/g, " ")}{ev.status === "archived" ? " · archived" : ""} · v{ev.triage.version}
                    </span>
                  ) : <span className="px-2 py-0.5 rounded-full text-[10px] bg-slate-100 text-slate-400">open</span>}
                </td>
                <td className="px-3 py-2">
                  <select value="" onChange={(e) => { if (e.target.value) { classifyOne(ev.event_id, e.target.value); e.target.value = ""; } }} data-testid={`triage-classify-${ev.event_id}`} className="h-8 px-2 rounded-md border border-slate-200 text-xs">
                    <option value="">{ev.triage ? "Reclassify…" : "Classify…"}</option>
                    {CLASSIFICATIONS.filter((c) => c !== "duplicate").map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] text-slate-400">
          Showing {skip + 1}–{skip + items.length} of {queue?.total ?? 0}. Raw security events are immutable & append-only —
          triage only records senior review decisions (versioned, audited). Archiving hides reviewed events from the default
          queue; nothing is ever deleted.
        </div>
        <div className="flex items-center gap-2" data-testid="triage-pagination">
          <button onClick={() => setSkip(Math.max(0, skip - 50))} disabled={skip === 0 || loading} data-testid="triage-prev"
            className="h-8 px-3 rounded-md border border-slate-200 text-sm disabled:opacity-40">Prev</button>
          <span className="text-xs text-slate-500">Page {Math.floor(skip / 50) + 1}</span>
          <button onClick={() => setSkip(skip + 50)} disabled={loading || (queue && skip + items.length >= queue.total)} data-testid="triage-next"
            className="h-8 px-3 rounded-md border border-slate-200 text-sm disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
